import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Teardrop pin (via border-radius + rotate) with the soundscape's own number
// inside, so visitors can match a pin on the map to its card below.
function numberedPinIcon(no: number) {
  const label = String(no).padStart(3, '0');
  return L.divIcon({
    className: '',
    html: `<div style="width:32px;height:32px;border-radius:50% 50% 50% 0;background:#0d9488;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.35);transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;">
      <span style="transform:rotate(45deg);color:#fff;font-weight:700;font-size:10px;font-family:sans-serif;letter-spacing:-0.5px;">${label}</span>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
}

interface MapPoint {
  no: number;
  name: string;
  description?: string;
  lat: number;
  lng: number;
}

// Fallback view for when there are no points yet (all-Minoh overview).
const MINOH_CENTER: [number, number] = [34.8275, 135.4706];
const MINOH_FALLBACK_ZOOM = 13;

export default function SoundscapeMap({ points }: { points: MapPoint[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: MINOH_CENTER,
      zoom: MINOH_FALLBACK_ZOOM,
      scrollWheelZoom: false,
    });
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    const markers: L.Marker[] = points.map((point) => {
      const marker = L.marker([point.lat, point.lng], { icon: numberedPinIcon(point.no) }).addTo(map);
      marker.bindPopup(
        `<div style="font-family:sans-serif;">
          <div style="font-weight:bold;color:#0d9488;font-size:12px;">No.${String(point.no).padStart(3, '0')}</div>
          <div style="font-weight:bold;margin-top:2px;">${point.name}</div>
          ${point.description ? `<div style="color:#666;font-size:12px;margin-top:4px;">${point.description}</div>` : ''}
          <button type="button" data-jump-to="${point.no}" style="margin-top:8px;background:#0d9488;color:#fff;border:none;border-radius:9999px;padding:5px 14px;font-size:12px;font-weight:600;cursor:pointer;">このカードを見る ↓</button>
        </div>`
      );
      // Popup content is raw HTML, so wire the button up for real once it's in the DOM.
      marker.on('popupopen', (e) => {
        const el = e.popup.getElement()?.querySelector<HTMLButtonElement>(`[data-jump-to="${point.no}"]`);
        el?.addEventListener('click', () => {
          document.getElementById(`soundscape-${point.no}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          map.closePopup();
        });
      });
      return marker;
    });

    // Frame whatever points exist rather than a fixed center/zoom, so the view
    // stays correct as more soundscapes (spread across all of Minoh) are added.
    const fitToMarkers = () => {
      map.invalidateSize();
      if (markers.length > 0) {
        const bounds = L.latLngBounds(markers.map((m) => m.getLatLng()));
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
      } else {
        map.setView(MINOH_CENTER, MINOH_FALLBACK_ZOOM);
      }
    };

    // Container size can still be settling (fonts/images above it loading) when
    // Leaflet first measures it, so re-fit a beat after mount too.
    fitToMarkers();
    const settleTimer = setTimeout(fitToMarkers, 150);
    const resizeObserver = new ResizeObserver(() => map.invalidateSize());
    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(settleTimer);
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, [points]);

  return <div ref={containerRef} className="w-full h-[400px]" />;
}
